// ** react imports
import React from 'react';

// ** react router imports
import { useNavigate } from 'react-router-dom';

// ** validation imports
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** custom hooks imports
import { useCreateMobileAppVersion } from '../../../hooks/apis/useMobileVersion';

// ** components imports
import LoadingButton from '../../../components/common/Buttons/LoadingButton';
import NormalButton from '../../../components/common/Buttons/NormalButton';
import NormalInput from '../../../components/common/Inputs/NormalInput';
import SelectInput from '../../../components/common/Inputs/SelectInput';
import SwitchInput from '../../../components/common/Inputs/SwitchInput';
import DynamicKeyValueInput from '../../../partials/DynamicKeyValueInput';

// ** enums imports
import { BUTTON_THEMES } from '../../../enums/common';

const AddMobileAppVersion = () => {
  // hooks
  const navigate = useNavigate();

  // validation schema
  const validationSchema = yup.object().shape({
    platform: yup
      .string()
      .required('Platform is required')
      .oneOf(
        ['ios', 'android'],
        "Invalid platform. Must be 'ios' or 'android'"
      ),
    currentVersion: yup.string().required('Current version is required').trim(),
    minimumSupportedVersion: yup
      .string()
      .required('Minimum supported version is required')
      .trim(),
    maximumSupportedVersion: yup.string().nullable().trim(),
    forceUpdate: yup.boolean().nullable(),
    updateUrl: yup.string().nullable().url('Update URL must be a valid URL'),
    releaseNotes: yup.string().nullable().trim(),
    changelog: yup.string().nullable().trim(),
    whatsNew: yup.string().nullable().trim(),
    isActive: yup.boolean().nullable(),
    isProduction: yup.boolean().nullable(),
    isBeta: yup.boolean().nullable(),
    targetAudience: yup
      .string()
      .nullable()
      .oneOf(
        ['all', 'beta-testers', 'early-access', 'premium-users'],
        'Invalid target audience'
      ),
    rolloutPercentage: yup
      .number()
      .nullable()
      .integer('Rollout percentage must be an integer')
      .min(0, 'Rollout percentage must be between 0 and 100')
      .max(100, 'Rollout percentage must be between 0 and 100'),
    expiryDate: yup
      .string()
      .nullable()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        'Expiry date must be in YYYY-MM-DD format'
      ),
    metadata: yup.object().nullable(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const { isCreateMobileAppVersionLoading, createMobileAppVersion } =
    useCreateMobileAppVersion();

  const onSubmit = async data => {
    try {
      const response = await createMobileAppVersion(data);

      if (response.data?.success) {
        reset();
        setTimeout(() => {
          navigate('/mobile-app-version/list');
        }, 1500);
      } else if (response.error) {
      }
    } catch (error) {
      console.error('Error creating mobile app version:', error);
    }
  };

  const handleCancel = () => {
    navigate('/mobile-app-version/list');
  };

  const renderErrorMessage = error => {
    if (!error) return null;
    return <p className="mt-2 text-xs text-red-500">{error}</p>;
  };

  const renderFormSection = (title, children, isExpanded = true) => (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg mb-6 overflow-hidden">
      <div className={`p-4 ${isExpanded ? '' : 'hidden'}`}>{children}</div>
    </div>
  );

  return (
    <div className="flex">
      <main className="grow">
        <div className="p-8 w-full mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
              Application Version
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Create a new mobile app version with all required details
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl mb-8">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="p-6 grid grid-cols-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {renderFormSection(
                    'Primary Information',
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <SelectInput
                          label="Platform"
                          name="platform"
                          options={[
                            { label: 'iOS', value: 'ios' },
                            { label: 'Android', value: 'android' },
                          ]}
                          register={register}
                          errors={errors.platform?.message}
                          required
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Current Version"
                          type="text"
                          name="currentVersion"
                          register={register}
                          errors={errors.currentVersion?.message}
                          required
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Minimum Supported Version"
                          type="text"
                          name="minimumSupportedVersion"
                          register={register}
                          errors={errors.minimumSupportedVersion?.message}
                          required
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Maximum Supported Version"
                          type="text"
                          name="maximumSupportedVersion"
                          register={register}
                          errors={errors.maximumSupportedVersion?.message}
                        />
                      </div>

                      <div>
                        <SwitchInput
                          label="Is Active"
                          name="isActive"
                          register={register}
                          errors={errors.isActive?.message}
                        />
                      </div>

                      <div>
                        <SwitchInput
                          label="Is Production"
                          name="isProduction"
                          register={register}
                          errors={errors.isProduction?.message}
                        />
                      </div>
                    </div>
                  )}

                  {renderFormSection(
                    'Targeting & Rollout',
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <SelectInput
                          label="Target Audience"
                          name="targetAudience"
                          options={[
                            { label: 'All Users', value: 'all' },
                            { label: 'Beta Testers', value: 'beta-testers' },
                            { label: 'Early Access', value: 'early-access' },
                            { label: 'Premium Users', value: 'premium-users' },
                          ]}
                          register={register}
                          errors={errors.targetAudience?.message}
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Rollout Percentage"
                          type="number"
                          name="rolloutPercentage"
                          register={register}
                          errors={errors.rolloutPercentage?.message}
                          min="0"
                          max="100"
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Expiry Date"
                          type="date"
                          name="expiryDate"
                          register={register}
                          errors={errors.expiryDate?.message}
                        />
                      </div>

                      <div></div>

                      <div>
                        <SwitchInput
                          label="Is Beta"
                          name="isBeta"
                          register={register}
                          errors={errors.isBeta?.message}
                        />
                      </div>

                      <div>
                        <SwitchInput
                          label="Force Update"
                          name="forceUpdate"
                          register={register}
                          errors={errors.forceUpdate?.message}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {renderFormSection(
                  'Update Settings',
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-1">
                      <NormalInput
                        label="Update URL"
                        type="url"
                        name="updateUrl"
                        register={register}
                        errors={errors.updateUrl?.message}
                      />
                    </div>

                    <div className="md:col-span-1">
                      <NormalInput
                        label="Changelog"
                        type="text"
                        name="changelog"
                        register={register}
                        errors={errors.changelog?.message}
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {renderFormSection(
                    'Advanced Settings',
                    <div className="space-y-1">
                      <label
                        htmlFor="metadata"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Metadata
                      </label>
                      <Controller
                        name="metadata"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <DynamicKeyValueInput
                            value={value ? value : {}}
                            onChange={onChange}
                            placeholder="Enter metadata as key-value pairs"
                            error={errors.metadata?.message}
                          />
                        )}
                      />
                    </div>,
                    true
                  )}

                  {renderFormSection(
                    'Advanced Settings',
                    <div className="space-y-1">
                      <label
                        htmlFor="metadata"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        What&apos;s New
                      </label>
                      <textarea
                        id="whatsNew"
                        name="whatsNew"
                        rows={4}
                        className={`w-full mt-1 form-textarea placeholder:text-sm dark:placeholder:text-gray-400 ${
                          errors.whatsNew
                            ? 'border-red-500 focus:ring-red-500 dark:border-red-600 dark:focus:ring-red-600'
                            : 'border-gray-300 focus:ring-blue-500 dark:border-gray-600 dark:focus:ring-blue-500'
                        } rounded-md shadow-sm`}
                        placeholder="Enter whats new"
                        {...register('whatsNew')}
                      />
                      {renderErrorMessage(errors.whatsNew?.message)}
                    </div>,
                    true
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <NormalButton
                    theme={BUTTON_THEMES.DANGER}
                    onClick={() => handleCancel()}
                  >
                    Cancel
                  </NormalButton>
                  <LoadingButton
                    type="submit"
                    theme={BUTTON_THEMES.PRIMARY}
                    isLoading={isCreateMobileAppVersionLoading}
                  >
                    Create Version
                  </LoadingButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddMobileAppVersion;
